import { Fragment, useEffect, useRef, useState } from "react";
import {
  ApiError,
  api,
  type CanalInteraccion,
  type CasoDetalle,
  type EstadoCaso,
  type EvidenciaOut,
  type Interaccion,
  type NotaOut,
  type PlantillaNotif,
  type UserMini,
} from "@/lib/api";
import { useAuth, canEdit, isAdmin } from "@/lib/auth";
import { esConversacion } from "@/lib/eventosNota";
import ContactoPicker from "@/components/ContactoPicker";
import ConfirmDialog from "@/ui/ConfirmDialog";
import Modal from "@/ui/Modal";
import AyudaVista from "@/ui/AyudaVista";
import { ayudaBitacora } from "@/components/ayuda/vistas/bitacora";
import FotosNota from "@/components/bitacora/FotosNota";
import ZonaFotos from "@/components/bitacora/ZonaFotos";
import { useFotosBitacora } from "@/components/bitacora/useFotosBitacora";
import {
  CANAL_LABEL,
  ESTADOS_BITACORA,
  minDateTimeLocal,
  REC_OPCIONES,
  recordatorioPayload,
  type RecCuando,
} from "@/components/bitacora/composer";
import { useToast } from "@/ui/Toast";
import Toggle from "@/ui/Toggle";
import { activarWebPush } from "@/lib/push";
import { ESTADO_IMEI_LABEL, ESTADO_IMEI_PILL, type EstadoImeiUI } from "@/lib/imei";
import { ESTADO_CASO_PILL, ESTADOS_CASO_BITACORA } from "@/lib/estadoCaso";
import { describeCuando } from "@/lib/fechas";
import NotaTexto from "@/components/NotaTexto";

type Props = {
  caso: CasoDetalle;
  onChanged: () => void;
  // Llevar a la pestaña Correos (desde el evento "correo enviado" de la bitácora);
  // si trae correoId, ese correo se abre y resalta.
  onVerCorreos?: (correoId?: number) => void;
};

// Item de la linea de tiempo de la bitacora: una nota o una llamada.
type TimelineItem =
  | { kind: "nota"; ts: string; data: NotaOut }
  | { kind: "interaccion"; ts: string; data: Interaccion };


export default function TabNotas({ caso, onChanged, onVerCorreos }: Props) {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  const [notas, setNotas] = useState<NotaOut[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtros de la bitácora (puro cliente; el GET ya trae todo).
  const [filtroTipo, setFiltroTipo] = useState<"todo" | "comentarios" | "sistema">("todo");
  const [filtroImei, setFiltroImei] = useState<number | "todos" | "general">("todos");

  // Crear
  const [texto, setTexto] = useState("");
  const [imeiId, setImeiId] = useState<number | "">("");
  const [submitting, setSubmitting] = useState(false);
  // Recordatorio opcional junto con la nota.
  const [recOn, setRecOn] = useState(false);
  const [recCuando, setRecCuando] = useState<RecCuando>("1h");
  const [recCustom, setRecCustom] = useState("");
  // Ref del textarea para devolverle el focus tras agregar — asi el user
  // puede seguir escribiendo notas en racha con Ctrl+Enter sin tener
  // que dar click cada vez (el re-fetch de la timeline pierde el focus).
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Menciones (@). Los chips son la FUENTE DE VERDAD de a quién se avisa
  // (los nombres traen espacios; no reparsear el texto). El texto solo es
  // legible para el humano.
  const [mencionables, setMencionables] = useState<UserMini[]>([]);
  // Mensajes rápidos (plantillas de notificación) para adjuntar a la nota por FK.
  const [plantillas, setPlantillas] = useState<PlantillaNotif[]>([]);
  const [plantillaNotifId, setPlantillaNotifId] = useState<number | "">("");
  // Fotos ya subidas al caso y pendientes de colgarse a la nota que se
  // está escribiendo. Se suben al soltarlas, no al guardar: así una nota
  // abandonada no pierde la evidencia (ya vive en la pestaña Evidencias).
  const [fotos, setFotos] = useState<EvidenciaOut[]>([]);
  const {
    agregar: agregarFotos,
    subiendo: subiendoFoto,
    arrastrando: arrastrandoFoto,
    dropProps: dropFotos,
  } = useFotosBitacora({ casoId: caso.id, fotos, onChange: setFotos });
  const [menciones, setMenciones] = useState<number[]>([]);
  // null = dropdown cerrado; "" o "texto" = abierto con ese query.
  const [mencionQuery, setMencionQuery] = useState<string | null>(null);
  const [mencionIndex, setMencionIndex] = useState(0);
  // Igual que slashQueryRef: reinicia el resaltado SOLO cuando cambia el query
  // (si no, el keyUp de la flecha lo regresaría a 0 y no se podría recorrer).
  const mencionQueryRef = useRef<string | null>(null);

  // Editar inline
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [editTexto, setEditTexto] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // Confirmar borrado
  const [confirmDel, setConfirmDel] = useState<NotaOut | null>(null);
  const [confirmLlamada, setConfirmLlamada] = useState<number | null>(null);

  // Interacciones (llamadas / contactos) + modal de "Registrar llamada"
  const [interacciones, setInteracciones] = useState<Interaccion[]>([]);
  const [regOpen, setRegOpen] = useState(false);
  const [regCanal, setRegCanal] = useState<CanalInteraccion>("videollamada");
  const [regCuando, setRegCuando] = useState("");
  const [regDuracion, setRegDuracion] = useState("");
  const [regConQuien, setRegConQuien] = useState("");
  const [regContactoId, setRegContactoId] = useState<number | null>(null);
  const [regResumen, setRegResumen] = useState("");
  const [regSubmitting, setRegSubmitting] = useState(false);

  // Req 7: cambio de estado de IMEI desde la bitácora (doble entrada / + chips).
  const [nuevoEstadoImei, setNuevoEstadoImei] = useState<EstadoImeiUI | "">("");
  // Parte C: estado del CASO desde la bitácora cuando el caso no tiene IMEIs.
  const [nuevoEstadoCaso, setNuevoEstadoCaso] = useState<EstadoCaso | "">("");
  const sinImeis = caso.imeis.length === 0;
  const [mostrarEstadoCtrl, setMostrarEstadoCtrl] = useState(false);
  const [mostrarEstadoCasoCtrl, setMostrarEstadoCasoCtrl] = useState(false);
  // Menú del trigger "/": null = cerrado; "" o "texto" = abierto con ese query.
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const [slashIndex, setSlashIndex] = useState(0);
  // Espeja slashQuery para reiniciar el resaltado del menú SOLO cuando cambia
  // lo tecleado tras la "/", no en cada tecla (si no, el keyUp de la flecha lo
  // regresaría a 0 y no se podría recorrer el menú con el teclado).
  const slashQueryRef = useRef<string | null>(null);
  // Refs por ítem para scrollIntoView del resaltado (menús con overflow).
  const slashItemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const mencionItemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Trae a la vista la opción resaltada del menú "/" al navegar con flechas.
  useEffect(() => {
    if (slashQuery !== null) {
      slashItemRefs.current[slashIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [slashIndex, slashQuery]);
  // Igual para el menú "@".
  useEffect(() => {
    if (mencionQuery !== null) {
      mencionItemRefs.current[mencionIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [mencionIndex, mencionQuery]);
  // Banner "Deshacer" tras un cambio de estado (guarda el estado previo).
  const [undo, setUndo] = useState<
    {
      label: string;
      prev: { id: number; estado: EstadoImeiUI | null }[];
      casoEstadoPrev: string;
    } | null
  >(null);

  async function load(quiet = false) {
    if (!quiet) setLoading(true);
    try {
      const [n, it] = await Promise.all([
        api.get<NotaOut[]>(`/api/casos/${caso.id}/notas`),
        api.get<Interaccion[]>(`/api/casos/${caso.id}/interacciones`),
      ]);
      setNotas(n);
      setInteracciones(it);
    } catch (err) {
      // En el refresh silencioso no molestamos con un toast por un fallo
      // transitorio de red — se reintenta en el proximo tick.
      if (!quiet) {
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("No pude cargar la bitacora", msg);
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  // Refetch automatico cuando el caso se actualiza por cualquier via:
  // hitos del PanelHitos, cambio de estado, asignacion, etc. cualquier PATCH
  // a /api/casos/{id} actualiza caso.updated_at, asi que esta dependencia
  // dispara el reload sin esperar a que el usuario refresque la pagina.
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id, caso.updated_at]);

  // Auto-refresh: cada 30s recarga la bitacora en SILENCIO para mostrar
  // notas/llamadas nuevas sin que tengas que refrescar. Es local — solo
  // actualiza esta lista; no recarga el caso ni el resto de la pagina, y no
  // toca lo que estes escribiendo o editando (eso es estado aparte). Se
  // pausa con la pestana en segundo plano y recarga al volver a ella.
  useEffect(() => {
    function tick() {
      if (!document.hidden) void load(true);
    }
    const id = setInterval(tick, 30_000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id]);

  // Mensajes rápidos (plantillas activas) para el picker de la nota.
  useEffect(() => {
    let vivo = true;
    api
      .get<PlantillaNotif[]>("/api/plantillas-notificacion")
      .then((ps) => {
        if (vivo) setPlantillas(ps);
      })
      .catch(() => {
        /* sin catálogo el picker no aparece; no es bloqueante */
      });
    return () => {
      vivo = false;
    };
  }, []);

  // Catálogo de mencionables (todos los activos). Se carga una vez y se
  // excluye a uno mismo (no tiene sentido auto-mencionarse).
  useEffect(() => {
    let vivo = true;
    api
      .get<UserMini[]>("/api/users/mencionables")
      .then((us) => {
        if (vivo) setMencionables(us.filter((u) => u.id !== me?.id));
      })
      .catch(() => {
        /* sin catálogo el picker simplemente no abre; no es bloqueante */
      });
    return () => {
      vivo = false;
    };
  }, [me?.id]);

  // Detecta el token @ JUSTO antes del caret. Acepta letras/acentos/ñ y
  // dígitos (\p{L}\p{N}); si no hay token abierto, cierra el dropdown.
  function detectarMencion(el: HTMLTextAreaElement) {
    const hasta = el.value.slice(0, el.selectionStart ?? el.value.length);
    const m = hasta.match(/@([\p{L}\p{N}_]*)$/u);
    const q = m ? m[1] : null;
    if (q !== mencionQueryRef.current) {
      mencionQueryRef.current = q;
      setMencionQuery(q);
      setMencionIndex(0);
    }
  }

  // Inserta @{nombre} en la posición del caret (reemplazando el token que se
  // venía tecleando) y agrega el id a los chips (fuente de verdad).
  function elegirMencion(u: UserMini) {
    const el = textareaRef.current;
    if (el) {
      const caret = el.selectionStart ?? el.value.length;
      const antes = el.value.slice(0, caret).replace(/@([\p{L}\p{N}_]*)$/u, "");
      const despues = el.value.slice(caret);
      const nuevo = `${antes}@${u.nombre} ${despues}`;
      setTexto(nuevo);
      // Recolocar el caret tras el nombre insertado (post-render).
      const pos = antes.length + u.nombre.length + 2;
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(pos, pos);
      }, 0);
    }
    setMenciones((prev) => (prev.includes(u.id) ? prev : [...prev, u.id]));
    mencionQueryRef.current = null;
    setMencionQuery(null);
  }

  function quitarMencion(id: number) {
    setMenciones((prev) => prev.filter((x) => x !== id));
  }

  // Detecta el token "/" al inicio de palabra justo antes del caret (Req 7).
  // Exige inicio de línea o espacio antes del "/" para no dispararse en URLs
  // (http://) ni fechas (16/6). Sin IMEIs ofrece estados del CASO (Parte C).
  function detectarSlash(el: HTMLTextAreaElement) {
    const hasta = el.value.slice(0, el.selectionStart ?? el.value.length);
    const m = hasta.match(/(?:^|\s)\/([a-zA-Z]*)$/);
    const q = m ? m[1] : null;
    // El resaltado (slashIndex) se reinicia SOLO cuando cambia el query (cambia la
    // lista de opciones). Reiniciarlo en cada tecla rompía la navegación: el keyUp
    // de la flecha lo regresaba a 0.
    if (q !== slashQueryRef.current) {
      slashQueryRef.current = q;
      setSlashQuery(q);
      setSlashIndex(0);
    }
  }

  // Llamado por los handlers del textarea: detecta @ (menciones) y / (estado).
  function detectarTokens(el: HTMLTextAreaElement) {
    detectarMencion(el);
    detectarSlash(el);
  }

  // El "/" ofrece TODO en un solo menú agrupado: estados del EQUIPO (si el caso
  // tiene IMEIs) y estados del CASO (siempre). Cada opción sabe a qué pertenece.
  type OpcionSlash = { kind: "equipo" | "caso"; estado: string };
  const opcionesSlash: OpcionSlash[] = [
    ...(sinImeis
      ? []
      : ESTADOS_BITACORA.map((e) => ({ kind: "equipo" as const, estado: e }))),
    ...ESTADOS_CASO_BITACORA.map((e) => ({ kind: "caso" as const, estado: e })),
  ];
  const labelOpcion = (o: OpcionSlash) =>
    o.kind === "equipo" ? ESTADO_IMEI_LABEL[o.estado as EstadoImeiUI] : o.estado;
  const pillOpcion = (o: OpcionSlash) =>
    o.kind === "equipo"
      ? ESTADO_IMEI_PILL[o.estado as EstadoImeiUI]
      : ESTADO_CASO_PILL[o.estado as EstadoCaso];
  // Opciones del menú "/" filtradas por lo tecleado (code o etiqueta).
  const slashOpciones =
    slashQuery === null
      ? []
      : opcionesSlash.filter(
          (o) =>
            o.estado.toLowerCase().includes(slashQuery.toLowerCase()) ||
            labelOpcion(o).toLowerCase().includes(slashQuery.toLowerCase()),
        );

  // Elige un estado desde el menú "/": borra el token "/…" del texto (conserva
  // el espacio previo) y fija el estado como metadato (no inyecta texto crudo).
  function elegirEstadoSlash(o: OpcionSlash) {
    const el = textareaRef.current;
    if (el) {
      const caret = el.selectionStart ?? el.value.length;
      const antes = el.value.slice(0, caret).replace(/(^|\s)\/([a-zA-Z]*)$/, "$1");
      const despues = el.value.slice(caret);
      setTexto(antes + despues);
      const pos = antes.length;
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(pos, pos);
      }, 0);
    }
    if (o.kind === "caso") setNuevoEstadoCaso(o.estado as EstadoCaso);
    else setNuevoEstadoImei(o.estado as EstadoImeiUI);
    slashQueryRef.current = null;
    setSlashQuery(null);
  }

  // Lista filtrada del dropdown (case-insensitive; query vacío = todos).
  const mencionesFiltradas =
    mencionQuery === null
      ? []
      : mencionables.filter((u) =>
          u.nombre.toLowerCase().includes(mencionQuery.toLowerCase()),
        );

  async function agregar() {
    // Una nota de SOLO fotos es válida (es constancia visual). Este guardia
    // tiene que coincidir con el `disabled` del botón: si se desincronizan, el
    // botón se ve activo y al darle clic no pasa nada — mordió al mover el
    // control de fotos a la fila (2026-08-11).
    if (!texto.trim() && plantillaNotifId === "" && fotos.length === 0) return;
    let recordatorio: Record<string, unknown> | undefined;
    if (recOn) {
      const rec = recordatorioPayload(recCuando, recCustom);
      if (!rec) {
        toast.error("Elige cuándo recordarte", "Falta la fecha del recordatorio");
        return;
      }
      recordatorio = rec;
    }
    // Req 7: captura el estado previo de los IMEIs objetivo para el "Deshacer".
    let undoPrev: { id: number; estado: EstadoImeiUI | null }[] | null = null;
    const estadoCasoAntes = caso.estado;
    if (nuevoEstadoImei !== "") {
      const objetivos =
        imeiId === ""
          ? caso.imeis.filter((i) => (i.estado_manual ?? null) !== "CRIT")
          : caso.imeis.filter((i) => i.id === imeiId);
      undoPrev = objetivos.map((i) => ({
        id: i.id,
        estado: (i.estado_manual ?? null) as EstadoImeiUI | null,
      }));
    }
    setSubmitting(true);
    try {
      if (recordatorio) {
        // Igual que la pestaña Recordatorios: pide permiso del navegador y
        // activa el web push para que el aviso llegue con la app cerrada.
        try {
          if (
            typeof Notification !== "undefined" &&
            Notification.permission === "default"
          ) {
            await Notification.requestPermission();
          }
        } catch {
          /* permiso denegado o no disponible: el aviso in-app sigue llegando */
        }
        void activarWebPush();
      }
      await api.post(`/api/casos/${caso.id}/notas`, {
        texto: texto.trim(),
        imei_id: imeiId === "" ? null : imeiId,
        ...(nuevoEstadoImei ? { nuevo_estado_imei: nuevoEstadoImei } : {}),
        ...(nuevoEstadoCaso ? { nuevo_estado_caso: nuevoEstadoCaso } : {}),
        ...(recordatorio ? { recordatorio } : {}),
        ...(menciones.length ? { menciones } : {}),
        ...(plantillaNotifId !== "" ? { plantilla_notif_id: plantillaNotifId } : {}),
        ...(fotos.length ? { evidencia_ids: fotos.map((f) => f.id) } : {}),
      });
      if (undoPrev && undoPrev.length > 0 && nuevoEstadoImei !== "") {
        setUndo({
          label: ESTADO_IMEI_LABEL[nuevoEstadoImei],
          prev: undoPrev,
          casoEstadoPrev: estadoCasoAntes,
        });
      }
      setTexto("");
      setImeiId("");
      setNuevoEstadoImei("");
      setNuevoEstadoCaso("");
      setSlashQuery(null);
      setRecOn(false);
      setRecCustom("");
      setMenciones([]);
      setPlantillaNotifId("");
      setFotos([]);
      setMencionQuery(null);
      // Refresh quiet — no muestra "Cargando bitácora..." para que la
      // timeline no parpadee al agregar. El parent (onChanged) tambien
      // hace su refresh silencioso (ver refreshSilencioso en CasoDetalle).
      await load(true);
      onChanged();
      toast.success(
        recordatorio ? "Nota y recordatorio creados" : "Nota agregada",
      );
      // Devolver el focus al textarea para escribir la siguiente sin
      // tener que dar click. setTimeout 0 espera a que termine el
      // re-render del refresh.
      setTimeout(() => textareaRef.current?.focus(), 0);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo agregar", msg);
    } finally {
      setSubmitting(false);
    }
  }

  // Revierte el último cambio de estado (banner Deshacer) usando el estado previo
  // capturado, vía el endpoint de estado existente.
  async function deshacerEstado() {
    if (!undo) return;
    try {
      await Promise.all(
        undo.prev.map((p) =>
          api.patch(`/api/casos/${caso.id}/imeis/${p.id}/estado`, { estado: p.estado }),
        ),
      );
      // Revierte también el estado del caso (si el cambio masivo lo había movido;
      // si no se movió, el PATCH es un no-op en el backend).
      await api.patch(`/api/casos/${caso.id}`, { estado: undo.casoEstadoPrev });
      toast.success("Cambio de estado deshecho");
      setUndo(null);
      await load(true);
      onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo deshacer", msg);
    }
  }

  function startEdit(n: NotaOut) {
    setEditandoId(n.id);
    setEditTexto(n.texto);
  }

  function cancelEdit() {
    setEditandoId(null);
    setEditTexto("");
  }

  async function saveEdit(n: NotaOut) {
    if (!editTexto.trim() || editTexto === n.texto) {
      cancelEdit();
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/api/casos/${caso.id}/notas/${n.id}`, {
        texto: editTexto.trim(),
      });
      toast.success("Nota actualizada");
      cancelEdit();
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    } finally {
      setSavingEdit(false);
    }
  }

  async function eliminar(n: NotaOut) {
    try {
      await api.del(`/api/casos/${caso.id}/notas/${n.id}`);
      toast.success("Nota eliminada");
      await load();
      onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo eliminar", msg);
    }
  }

  // Abre el modal de "Registrar llamada" con la fecha pre-cargada (ahora).
  function abrirRegistro() {
    const t = new Date();
    const local = new Date(t.getTime() - t.getTimezoneOffset() * 60000);
    setRegCuando(local.toISOString().slice(0, 16));
    setRegCanal("videollamada");
    setRegDuracion("");
    setRegConQuien("");
    setRegContactoId(null);
    setRegResumen("");
    setRegOpen(true);
  }

  async function crearLlamada() {
    if (!regResumen.trim() || !regCuando) return;
    setRegSubmitting(true);
    try {
      await api.post(`/api/casos/${caso.id}/interacciones`, {
        canal: regCanal,
        ocurrida_at: new Date(regCuando).toISOString(),
        duracion_min: regDuracion ? Number(regDuracion) : null,
        con_quien: regConQuien.trim() || null,
        contacto_id: regContactoId,
        resumen: regResumen.trim(),
      });
      toast.success("Llamada registrada");
      setRegOpen(false);
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude registrar", msg);
    } finally {
      setRegSubmitting(false);
    }
  }

  async function borrarLlamada(id: number) {
    try {
      await api.del(`/api/casos/${caso.id}/interacciones/${id}`);
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude eliminar", msg);
    }
  }

  // Notas de creacion y cierre del caso son audit trail inmutable —
  // representan momentos del ciclo de vida del caso que no se pueden revertir.
  function esInmutable(n: NotaOut): boolean {
    return n.tags.includes("creacion") || n.tags.includes("cierre");
  }

  const casoCerrado = caso.estado === "Cerrado";

  function puedeEditar(n: NotaOut): boolean {
    if (!canEdit(me)) return false;
    if (esInmutable(n)) return false;
    if (casoCerrado) return false;
    return n.autor_id === me?.id || isAdmin(me);
  }

  function puedeEliminar(n: NotaOut): boolean {
    if (casoCerrado) return false;
    return isAdmin(me) && !esInmutable(n);
  }

  // Filtro de notas: por naturaleza (conversación vs cambios del expediente) y
  // por IMEI asociado. Ojo: NO es por `tipo` — el vaivén con el proveedor lo
  // escribe un worker (tipo=sistema) pero es conversación. Ver lib/eventosNota.
  const notasFiltradas = notas.filter((n) => {
    const conversacion = esConversacion(n.tipo, n.evento);
    if (filtroTipo === "comentarios" && !conversacion) return false;
    if (filtroTipo === "sistema" && conversacion) return false;
    if (filtroImei === "general" && n.imei_id != null) return false;
    if (typeof filtroImei === "number" && n.imei_id !== filtroImei) return false;
    return true;
  });
  // Las llamadas no son del sistema ni están ligadas a un IMEI: se muestran
  // salvo cuando filtras "Eventos del sistema" o por un IMEI específico.
  const mostrarInteracciones =
    filtroTipo !== "sistema" && (filtroImei === "todos" || filtroImei === "general");

  // Linea de tiempo: notas (filtradas) + llamadas, mas reciente primero.
  const timeline: TimelineItem[] = [
    ...notasFiltradas.map((n) => ({ kind: "nota" as const, ts: n.ts, data: n })),
    ...(mostrarInteracciones ? interacciones : []).map((it) => ({
      kind: "interaccion" as const,
      ts: it.ocurrida_at,
      data: it,
    })),
  ].sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <AyudaVista
          titulo="Bitácora interna"
          {...ayudaBitacora({
            puedeEscribir: canEdit(me) && !casoCerrado,
            puedeEditar: canEdit(me),
            esAdmin: isAdmin(me),
            cerrado: casoCerrado,
            conImeis: !sinImeis,
            conMensajesRapidos: plantillas.length > 0,
          })}
        />
      </div>
      {canEdit(me) && !casoCerrado && (
        <div
          className={
            "card space-y-2 transition " +
            (arrastrandoFoto ? "ring-2 ring-accent-500" : "")
          }
          {...dropFotos}
        >
          <p className="text-xs text-zinc-500">
            Bitácora interna del caso — anota pruebas, intentos, llamadas o lo
            que vayas haciendo. <strong>NO</strong> se manda al cliente. Para
            escribirle al cliente usa la pestaña "Correos" (botón Enviar
            correo).{" "}
            <span className="text-zinc-600">
              Atajo: <kbd className="px-1 py-0.5 rounded border border-ink-700 bg-ink-800 text-[10px]">Ctrl</kbd>+<kbd className="px-1 py-0.5 rounded border border-ink-700 bg-ink-800 text-[10px]">Enter</kbd> guarda.
            </span>{" "}
            Escribe <strong>@</strong> para avisarle a un compañero (queda
            pendiente hasta que responda).
          </p>
          <div className="relative">
            <textarea
              ref={textareaRef}
              className="input min-h-[80px]"
              placeholder="Anota un avance, prueba, llamada... (@ para avisar a un compañero)"
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                detectarTokens(e.target);
              }}
              onKeyUp={(e) => detectarTokens(e.currentTarget)}
              onClick={(e) => detectarTokens(e.currentTarget)}
              onKeyDown={(e) => {
                // Menú "/" abierto: ↑/↓ recorren, Enter elige el resaltado,
                // Escape cierra. El resaltado persiste entre teclas (ver
                // detectarSlash) para poder recorrer el menú con el teclado.
                if (slashQuery !== null && slashOpciones.length > 0) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setSlashIndex((i) => (i + 1) % slashOpciones.length);
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setSlashIndex((i) => (i - 1 + slashOpciones.length) % slashOpciones.length);
                    return;
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    elegirEstadoSlash(
                      slashOpciones[Math.min(slashIndex, slashOpciones.length - 1)],
                    );
                    return;
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    slashQueryRef.current = null;
                    setSlashQuery(null);
                    return;
                  }
                }
                // Menú "@" abierto: ↑/↓ recorren, Enter elige el resaltado,
                // Escape cierra (mismo patrón que "/").
                if (mencionQuery !== null && mencionesFiltradas.length > 0) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setMencionIndex((i) => (i + 1) % mencionesFiltradas.length);
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setMencionIndex(
                      (i) => (i - 1 + mencionesFiltradas.length) % mencionesFiltradas.length,
                    );
                    return;
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    elegirMencion(
                      mencionesFiltradas[Math.min(mencionIndex, mencionesFiltradas.length - 1)],
                    );
                    return;
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    mencionQueryRef.current = null;
                    setMencionQuery(null);
                    return;
                  }
                }
                // Esc cierra el dropdown de menciones aunque no haya coincidencias.
                if (e.key === "Escape" && mencionQuery !== null) {
                  e.preventDefault();
                  e.stopPropagation();
                  mencionQueryRef.current = null;
                  setMencionQuery(null);
                  return;
                }
                // Ctrl+Enter o Cmd+Enter dispara "Agregar a bitácora".
                // No interceptamos Enter normal — el textarea sigue siendo
                // multiline para notas con saltos. preventDefault evita
                // que browsers traten Ctrl+Enter como submit del form
                // (comportamiento HTML estandar).
                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  if (texto.trim() && !submitting) void agregar();
                }
              }}
            />
            {/* Dropdown del picker @ — panel absoluto bajo el textarea. */}
            {mencionQuery !== null && (
              <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-md border border-ink-700 bg-ink-900 shadow-lg">
                {mencionesFiltradas.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-zinc-500">
                    Nadie coincide con "{mencionQuery}".
                  </p>
                ) : (
                  mencionesFiltradas.map((u, idx) => (
                    <button
                      key={u.id}
                      type="button"
                      ref={(el) => {
                        mencionItemRefs.current[idx] = el;
                      }}
                      // onMouseDown (no onClick): dispara antes de que el
                      // textarea pierda el focus, así no se cierra el panel
                      // por el blur antes de insertar.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        elegirMencion(u);
                      }}
                      className={`flex w-full flex-col items-start px-3 py-2 text-left hover:bg-ink-800 cursor-pointer ${
                        idx === mencionIndex ? "bg-ink-800" : ""
                      }`}
                    >
                      <span className="text-sm text-zinc-200">{u.nombre}</span>
                      <span className="text-[10px] uppercase tracking-wide text-zinc-500">
                        {u.rol}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
            {/* Dropdown del trigger "/" — menú agrupado: equipo + caso. */}
            {slashQuery !== null && slashOpciones.length > 0 && (
              <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-md border border-ink-700 bg-ink-900 shadow-lg">
                {slashOpciones.map((o, idx) => {
                  const encabezado =
                    idx === 0 || slashOpciones[idx - 1].kind !== o.kind;
                  return (
                    <Fragment key={`${o.kind}-${o.estado}`}>
                      {encabezado && (
                        <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-zinc-500 border-b border-ink-700 sticky top-0 bg-ink-900">
                          Estado del {o.kind === "equipo" ? "equipo" : "caso"}
                        </p>
                      )}
                      <button
                        type="button"
                        ref={(el) => {
                          slashItemRefs.current[idx] = el;
                        }}
                        onMouseDown={(ev) => {
                          ev.preventDefault();
                          elegirEstadoSlash(o);
                        }}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-ink-800 cursor-pointer ${
                          idx === slashIndex ? "bg-ink-800" : ""
                        }`}
                      >
                        <span
                          className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${pillOpcion(o)}`}
                        >
                          {o.estado}
                        </span>
                        {labelOpcion(o) !== o.estado && (
                          <span className="text-sm text-zinc-200">{labelOpcion(o)}</span>
                        )}
                      </button>
                    </Fragment>
                  );
                })}
              </div>
            )}
          </div>
          {/* Chips = fuente de verdad de a quién se avisa. */}
          {menciones.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wide text-zinc-500">
                Avisar a:
              </span>
              {menciones.map((id) => {
                const u = mencionables.find((x) => x.id === id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-accent-500/30 bg-accent-500/10 text-accent-300"
                  >
                    @{u?.nombre ?? `#${id}`}
                    <button
                      type="button"
                      onClick={() => quitarMencion(id)}
                      aria-label={`Quitar a ${u?.nombre ?? id}`}
                      className="text-accent-400 hover:text-accent-200"
                    >
                      ✕
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          {plantillaNotifId !== "" && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wide text-zinc-500">
                Mensaje rápido:
              </span>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 max-w-full">
                <span className="truncate">
                  💬 {plantillas.find((p) => p.id === plantillaNotifId)?.texto}
                </span>
                <button
                  type="button"
                  onClick={() => setPlantillaNotifId("")}
                  aria-label="Quitar mensaje rápido"
                  className="text-emerald-300 hover:text-emerald-100 flex-shrink-0"
                >
                  ✕
                </button>
              </span>
            </div>
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap">
            <select
              className="input w-full sm:w-auto sm:max-w-[280px] text-sm"
              value={imeiId}
              onChange={(e) => setImeiId(e.target.value ? parseInt(e.target.value) : "")}
            >
              <option value="">General del caso</option>
              {caso.imeis.map((i) => (
                <option key={i.id} value={i.id}>IMEI {i.imei}</option>
              ))}
            </select>
            {plantillas.length > 0 && (
              <select
                className="input w-full sm:w-auto sm:max-w-[280px] text-sm"
                value={plantillaNotifId}
                onChange={(e) =>
                  setPlantillaNotifId(e.target.value ? parseInt(e.target.value) : "")
                }
                aria-label="Mensaje rápido"
              >
                <option value="">💬 Mensaje rápido…</option>
                {plantillas.map((pl) => (
                  <option key={pl.id} value={pl.id}>
                    {pl.texto.length > 60 ? pl.texto.slice(0, 60) + "…" : pl.texto}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={agregar}
              disabled={
                submitting ||
                (!texto.trim() && plantillaNotifId === "" && fotos.length === 0)
              }
              className="btn-primary disabled:opacity-50 w-full sm:w-auto"
            >
              {submitting ? "Guardando..." : "Agregar a bitácora"}
            </button>
            <Toggle
              checked={recOn}
              onChange={setRecOn}
              label="⏰ Recordatorio"
              hint="Se crea junto con la nota"
            />
            <button
              type="button"
              onClick={abrirRegistro}
              className="btn-ghost text-sm border border-ink-600 w-full sm:w-auto"
            >
              Registrar llamada
            </button>
            <ZonaFotos
              casoId={caso.id}
              fotos={fotos}
              onChange={setFotos}
              agregar={agregarFotos}
              subiendo={subiendoFoto}
              disabled={submitting}
            />
          </div>
          {recOn && (
            <div className="border-t border-ink-700 pt-2 space-y-2">
              <p className="text-xs text-zinc-500">
                ¿Cuándo te recuerdo? El recordatorio usa el texto de la nota y
                avisa a quien abrió el caso y al asignado (lo editas luego en la
                pestaña Recordatorios).
              </p>
              <div className="flex flex-wrap gap-1.5">
                {REC_OPCIONES.map((o) => (
                  <button
                    key={o.val}
                    type="button"
                    onClick={() => setRecCuando(o.val)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition ${
                      recCuando === o.val
                        ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                        : "border-ink-600 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {recCuando === "custom" && (
                <input
                  type="datetime-local"
                  className="input text-sm w-full sm:w-auto"
                  min={minDateTimeLocal()}
                  value={recCustom}
                  onChange={(e) => setRecCustom(e.target.value)}
                />
              )}
            </div>
          )}
          {caso.imeis.length > 0 && (
            <div className="border-t border-ink-700 pt-2 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMostrarEstadoCtrl((v) => !v)}
                  className="text-xs text-zinc-400 hover:text-white"
                >
                  {mostrarEstadoCtrl ? "− " : "± "}Cambiar estado del equipo
                </button>
                {nuevoEstadoImei !== "" && (
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${ESTADO_IMEI_PILL[nuevoEstadoImei]}`}
                  >
                    → {ESTADO_IMEI_LABEL[nuevoEstadoImei]}
                    <button
                      type="button"
                      onClick={() => setNuevoEstadoImei("")}
                      aria-label="Quitar el cambio de estado"
                      className="hover:opacity-70"
                    >
                      ✕
                    </button>
                  </span>
                )}
                <span className="text-[10px] text-zinc-600">
                  o escribe{" "}
                  <kbd className="px-1 rounded border border-ink-700 bg-ink-800">/</kbd> en la
                  nota
                </span>
              </div>
              {mostrarEstadoCtrl && (
                <div className="flex flex-wrap gap-1">
                  {ESTADOS_BITACORA.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setNuevoEstadoImei(e)}
                      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border transition ${
                        nuevoEstadoImei === e
                          ? ESTADO_IMEI_PILL[e]
                          : "border-ink-600 text-zinc-400 hover:text-white"
                      }`}
                    >
                      {e}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setNuevoEstadoImei("")}
                    className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border transition ${
                      nuevoEstadoImei === ""
                        ? "bg-zinc-500/15 text-zinc-200 border-zinc-500/40"
                        : "border-ink-600 text-zinc-400 hover:text-white"
                    }`}
                  >
                    Sin cambio
                  </button>
                </div>
              )}
              {nuevoEstadoImei !== "" && imeiId === "" && (
                <p className="text-[11px] text-amber-300">
                  ⚠ Se aplicará a los {caso.imeis.length} equipos del caso (los marcados CRIT se
                  respetan).
                </p>
              )}
              {nuevoEstadoImei !== "" && imeiId !== "" && (
                <p className="text-[11px] text-zinc-500">
                  Se aplicará solo al IMEI seleccionado arriba.
                </p>
              )}
            </div>
          )}
            <div className="border-t border-ink-700 pt-2 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMostrarEstadoCasoCtrl((v) => !v)}
                  className="text-xs text-zinc-400 hover:text-white"
                >
                  {mostrarEstadoCasoCtrl ? "− " : "± "}Cambiar estado del caso
                </button>
                {nuevoEstadoCaso !== "" && (
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${ESTADO_CASO_PILL[nuevoEstadoCaso]}`}
                  >
                    → {nuevoEstadoCaso}
                    <button
                      type="button"
                      onClick={() => setNuevoEstadoCaso("")}
                      aria-label="Quitar el cambio de estado"
                      className="hover:opacity-70"
                    >
                      ✕
                    </button>
                  </span>
                )}
                <span className="text-[10px] text-zinc-600">
                  o escribe{" "}
                  <kbd className="px-1 rounded border border-ink-700 bg-ink-800">/</kbd> en
                  la nota
                </span>
              </div>
              {mostrarEstadoCasoCtrl && (
                <div className="flex flex-wrap gap-1">
                  {ESTADOS_CASO_BITACORA.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setNuevoEstadoCaso(e)}
                      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border transition ${
                        nuevoEstadoCaso === e
                          ? ESTADO_CASO_PILL[e]
                          : "border-ink-600 text-zinc-400 hover:text-white"
                      }`}
                    >
                      {e}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setNuevoEstadoCaso("")}
                    className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border transition ${
                      nuevoEstadoCaso === ""
                        ? "bg-zinc-500/15 text-zinc-200 border-zinc-500/40"
                        : "border-ink-600 text-zinc-400 hover:text-white"
                    }`}
                  >
                    Sin cambio
                  </button>
                </div>
              )}
              {nuevoEstadoCaso !== "" && (
                <p className="text-[11px] text-zinc-500">
                  Cambia el <strong>estado del caso</strong>
                  {sinImeis
                    ? " (no tiene equipos)"
                    : " — no toca el estado de los equipos"}
                  . Tu nota queda como justificación.
                </p>
              )}
            </div>
        </div>
      )}

      {undo && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-amber-200">
            Cambié {undo.prev.length} equipo{undo.prev.length === 1 ? "" : "s"} a "{undo.label}".
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void deshacerEstado()}
              className="btn-ghost text-xs border border-amber-500/40 text-amber-200"
            >
              Deshacer
            </button>
            <button
              type="button"
              onClick={() => setUndo(null)}
              aria-label="Cerrar aviso"
              className="btn-ghost text-xs"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {canEdit(me) && casoCerrado && (
        <div className="card">
          <p className="text-xs text-zinc-400">
            El caso está <strong className="text-zinc-200">cerrado</strong> —
            la bitácora queda como foto del seguimiento. Para continuar,
            abre un <em>Caso relacionado</em> desde el encabezado.
          </p>
        </div>
      )}

      {!loading && notas.length + interacciones.length > 0 && (
        <div className="card flex flex-col gap-2 text-xs sm:flex-row sm:items-center sm:flex-wrap">
          <span className="text-[10px] uppercase tracking-wide text-zinc-500">Filtrar</span>
          <div className="flex flex-wrap gap-1">
            {(
              [
                ["todo", "Todo"],
                ["comentarios", "Conversación"],
                ["sistema", "Eventos del sistema"],
              ] as const
            ).map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setFiltroTipo(v)}
                className={`px-2 py-1 rounded-full border transition ${
                  filtroTipo === v
                    ? "bg-accent-500/15 text-accent-300 border-accent-500/40"
                    : "border-ink-600 text-zinc-400 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          {caso.imeis.length > 0 && (
            <select
              className="input text-xs py-1 w-full sm:w-auto sm:max-w-[220px]"
              value={String(filtroImei)}
              onChange={(e) => {
                const v = e.target.value;
                setFiltroImei(v === "todos" || v === "general" ? v : Number(v));
              }}
            >
              <option value="todos">Todos los equipos</option>
              <option value="general">General del caso</option>
              {caso.imeis.map((i) => (
                <option key={i.id} value={i.id}>
                  IMEI {i.imei}
                </option>
              ))}
            </select>
          )}
          <span className="text-zinc-500 sm:ml-auto">
            mostrando {timeline.length} de {notas.length + interacciones.length}
          </span>
        </div>
      )}

      <div className="card">
        {loading ? (
          <p className="text-sm text-zinc-500">Cargando bitácora...</p>
        ) : timeline.length === 0 ? (
          <p className="text-sm text-zinc-500">Sin notas ni llamadas todavía.</p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {timeline.map((item) => {
              if (item.kind === "interaccion") {
                const it = item.data;
                return (
                  <li key={`it-${it.id}`} className="py-3 px-2 -mx-2 rounded-md transition-colors hover:bg-white/5">
                    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between sm:gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300">
                            {CANAL_LABEL[it.canal]}
                          </span>
                          {it.duracion_min != null && (
                            <span className="text-xs text-zinc-500">
                              {it.duracion_min} min
                            </span>
                          )}
                          {it.con_quien && (
                            <span className="text-xs text-zinc-500">
                              con {it.con_quien}
                            </span>
                          )}
                        </div>
                        <NotaTexto texto={it.resumen} className="mt-1" />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-zinc-500 sm:block sm:space-y-0.5 sm:text-right sm:text-xs sm:flex-shrink-0">
                        <div>{it.registrado_por_nombre || "—"}</div>
                        <div>{new Date(it.ocurrida_at).toLocaleString()}</div>
                        {canEdit(me) && (
                          <div className="pt-1">
                            <button
                              onClick={() => setConfirmLlamada(it.id)}
                              className="text-red-400 hover:text-red-300 text-xs"
                            >
                              Eliminar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              }

              const n = item.data;
              return (
                <li key={`n-${n.id}`} className="py-3 px-2 -mx-2 rounded-md transition-colors hover:bg-white/5">
                  {editandoId === n.id ? (
                    <div className="space-y-2">
                      <textarea
                        className="input min-h-[80px]"
                        value={editTexto}
                        onChange={(e) => setEditTexto(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Escape") cancelEdit();
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                            saveEdit(n);
                          }
                        }}
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => saveEdit(n)}
                          disabled={savingEdit || !editTexto.trim()}
                          className="btn-primary text-xs disabled:opacity-50"
                        >
                          {savingEdit ? "Guardando..." : "Guardar"}
                        </button>
                        <button onClick={cancelEdit} className="btn-ghost text-xs">
                          Cancelar
                        </button>
                        <span className="text-xs text-zinc-500">
                          Ctrl+Enter para guardar, Esc para cancelar
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between sm:gap-2">
                      <div className="flex-1 min-w-0">
                        {n.mensaje_rapido && (
                          <div className="text-sm text-emerald-200/90 whitespace-pre-wrap rounded border border-emerald-500/20 bg-emerald-500/5 px-2 py-1">
                            💬 {n.mensaje_rapido}
                          </div>
                        )}
                        {n.texto && <NotaTexto texto={n.texto} />}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {n.evento === "correo.enviado" && onVerCorreos && (
                            <button
                              type="button"
                              onClick={() =>
                                onVerCorreos(
                                  typeof n.meta?.correo_id === "number"
                                    ? n.meta.correo_id
                                    : undefined,
                                )
                              }
                              className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20 transition"
                            >
                              ✉️ ver en correos →
                            </button>
                          )}
                          {n.imei != null && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300 font-mono">
                              IMEI {n.imei}
                            </span>
                          )}
                          {n.recordatorio_id != null && (
                            <span className={
                              "text-[10px] uppercase tracking-wide inline-flex items-center gap-1 px-1.5 py-0.5 rounded border " +
                              (n.recordatorio_estado === "completado" || n.recordatorio_estado === "cancelado"
                                ? "border-zinc-600/40 bg-zinc-600/10 text-zinc-500 line-through"
                                : "border-amber-500/30 bg-amber-500/10 text-amber-300")
                            }>
                              ⏰ {n.recordatorio_remind_at
                                ? new Date(n.recordatorio_remind_at).toLocaleString()
                                : "recordatorio"}
                              {n.recordatorio_remind_at && n.recordatorio_estado === "pendiente" && (
                                <span className="normal-case text-zinc-400">
                                  · {describeCuando(n.recordatorio_remind_at)}
                                </span>
                              )}
                            </span>
                          )}
                          {n.tags.map((t) => (
                            <span
                              key={t}
                              className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-accent-500/30 bg-accent-500/10 text-accent-300"
                            >
                              #{t}
                            </span>
                          ))}
                        </div>
                        <FotosNota fotos={n.evidencias ?? []} />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-zinc-500 sm:block sm:space-y-0.5 sm:text-right sm:text-xs sm:flex-shrink-0">
                        <div>{n.autor_nombre || "sistema"}</div>
                        <div>{new Date(n.ts).toLocaleString()}</div>
                        {n.tipo !== "manual" && (
                          <div className="uppercase tracking-wide text-[10px]">
                            {n.tipo}
                          </div>
                        )}
                        <div className="space-x-1 pt-1">
                          {puedeEditar(n) && (
                            <button
                              onClick={() => startEdit(n)}
                              className="text-zinc-400 hover:text-white text-xs"
                            >
                              Editar
                            </button>
                          )}
                          {puedeEliminar(n) && (
                            <button
                              onClick={() => setConfirmDel(n)}
                              className="text-red-400 hover:text-red-300 text-xs"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {confirmLlamada !== null && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmLlamada(null)}
          title="Eliminar llamada"
          description="La llamada se borra de la bitácora y no se puede recuperar."
          variant="danger"
          confirmLabel="Eliminar"
          onConfirm={() => borrarLlamada(confirmLlamada)}
        />
      )}

      {confirmDel && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDel(null)}
          title="Eliminar nota"
          description="Esta nota se borra permanentemente. Las notas de creación y cierre del caso son inmutables."
          variant="danger"
          confirmLabel="Eliminar"
          onConfirm={() => eliminar(confirmDel)}
        />
      )}

      {/* Modal "Registrar llamada" — formulario en modal para no meter otro
          panel fijo en la pantalla. */}
      <Modal
        open={regOpen}
        onOpenChange={setRegOpen}
        title="Registrar llamada"
        description="Registra un contacto con el cliente (videollamada, llamada, etc.). Queda en la bitácora del caso."
        size="lg"
        footer={
          <>
            <button
              onClick={() => setRegOpen(false)}
              className="btn-ghost text-sm"
            >
              Cancelar
            </button>
            <button
              onClick={crearLlamada}
              disabled={regSubmitting || !regResumen.trim() || !regCuando}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {regSubmitting ? "Guardando..." : "Guardar"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Canal</label>
              <select
                value={regCanal}
                onChange={(e) => setRegCanal(e.target.value as CanalInteraccion)}
                className="input w-full text-sm"
              >
                {Object.entries(CANAL_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">
                ¿Cuándo ocurrió?
              </label>
              <input
                type="datetime-local"
                value={regCuando}
                onChange={(e) => setRegCuando(e.target.value)}
                className="input w-full text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">
                Duración (min)
              </label>
              <input
                type="number"
                min={0}
                placeholder="Opcional"
                value={regDuracion}
                onChange={(e) => setRegDuracion(e.target.value)}
                className="input w-full text-sm"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Con quién</label>
            <ContactoPicker
              clienteId={caso.cliente_id}
              contactoId={regContactoId}
              conQuien={regConQuien}
              onChange={({ contactoId, conQuien }) => {
                setRegContactoId(contactoId);
                setRegConQuien(conQuien);
              }}
              prefillFromCaso={{
                nombre: caso.contacto_nombre,
                email: caso.contacto_email,
                telefono: caso.contacto_telefono,
              }}
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Resumen</label>
            <textarea
              className="input w-full min-h-[110px]"
              placeholder="Qué se habló, acuerdos, pendientes..."
              value={regResumen}
              onChange={(e) => setRegResumen(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
